/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { fetchMarketBreadth } from "../../api";

const MarketBreadth = () => {
    const [data, setData] = useState(null);

    useEffect(() => {

        const fetchData = async () => {

            fetchMarketBreadth()
                .then((data) => setData(data))
                .catch((error) =>
                    console.error(
                        "Error fetching market breadth:",
                        error
                    )
                );
        };

        fetchData();

        const interval = setInterval(
            fetchData,
            30000
        );

        return () => clearInterval(interval);

    }, []);

    if (!data) {

        return (
            <div className="p-8 text-center text-slate-500">
                Loading market breadth...
            </div>
        );

    }

    const sentimentColor =
        data.market_sentiment === "Bullish"
            ? "text-emerald-600"
            : data.market_sentiment === "Bearish"
                ? "text-red-600"
                : "text-amber-600";

    return (

        <div className="space-y-6">

            {/* HEADER */}

            <div
                className="
                    bg-white
                    rounded-2xl
                    border
                    border-slate-200
                    shadow-sm
                    p-6
                "
            >

                <h2
                    className="
                        text-2xl
                        font-bold
                        text-slate-800
                        mb-2
                    "
                >
                    Market Breadth Overview
                </h2>

                <p
                    className={`
                        text-lg
                        font-semibold
                        ${sentimentColor}
                    `}
                >
                    Market Sentiment:
                    {" "}
                    {data.market_sentiment}
                </p>

            </div>

            {/* TOP CHARTS */}

            <div
                className="
                    grid
                    grid-cols-1
                    xl:grid-cols-2
                    gap-6
                "
            >

                <div
                    className="
                        bg-white
                        rounded-2xl
                        border
                        border-slate-200
                        shadow-sm
                        p-4
                    "
                >

                    <Plot
                        data={[
                            {
                                labels: [
                                    `Advancing (${data.advancing_stocks})`,
                                    `Declining (${data.declining_stocks})`,
                                    `Unchanged (${data.unchanged_stocks})`,
                                ],
                                values: [
                                    data.advancing_stocks,
                                    data.declining_stocks,
                                    data.unchanged_stocks,
                                ],
                                type: "pie",
                                marker: {
                                    colors: [
                                        "#10b981",
                                        "#ef4444",
                                        "#3b82f6",
                                    ],
                                },
                            },
                        ]}
                        layout={{
                            title: "Market Breadth Distribution",
                            autosize: true,
                            height: 400,
                        }}
                        style={{
                            width: "100%",
                        }}
                        config={{
                            responsive: true,
                        }}
                    />

                </div>

                <div
                    className="
                        bg-white
                        rounded-2xl
                        border
                        border-slate-200
                        shadow-sm
                        p-4
                    "
                >

                    <Plot
                        data={[
                            {
                                x: ["Advance - Decline"],
                                y: [data.ad_line],
                                type: "bar",
                                marker: {
                                    color:
                                        data.ad_line > 0
                                            ? "#10b981"
                                            : "#ef4444",
                                },
                            },
                        ]}
                        layout={{
                            title: "Advance / Decline Line",
                            autosize: true,
                            height: 400,
                        }}
                        style={{
                            width: "100%",
                        }}
                        config={{
                            responsive: true,
                        }}
                    />

                </div>

            </div>

            {/* SECOND ROW */}

            <div
                className="
                    grid
                    grid-cols-1
                    xl:grid-cols-2
                    gap-6
                "
            >

                <div
                    className="
                        bg-white
                        rounded-2xl
                        border
                        border-slate-200
                        shadow-sm
                        p-4
                    "
                >

                    <Plot
                        data={[
                            {
                                x: [
                                    "Up Days",
                                    "Down Days",
                                ],
                                y: [
                                    data.percent_up_days,
                                    data.percent_down_days,
                                ],
                                type: "bar",
                                marker: {
                                    color: [
                                        "#10b981",
                                        "#ef4444",
                                    ],
                                },
                            },
                        ]}
                        layout={{
                            title:
                                "% Up Days vs % Down Days (20 Sessions)",
                            autosize: true,
                            height: 400,
                        }}
                        style={{
                            width: "100%",
                        }}
                        config={{
                            responsive: true,
                        }}
                    />

                </div>

                <div
                    className="
                        bg-white
                        rounded-2xl
                        border
                        border-slate-200
                        shadow-sm
                        p-4
                    "
                >

                    <Plot
                        data={[
                            {
                                x: Object.keys(
                                    data.most_active_stocks
                                ),
                                y: Object.values(
                                    data.most_active_stocks
                                ),
                                type: "bar",
                            },
                        ]}
                        layout={{
                            title:
                                "Most Active Stocks",
                            autosize: true,
                            height: 400,
                        }}
                        style={{
                            width: "100%",
                        }}
                        config={{
                            responsive: true,
                        }}
                    />

                </div>

            </div>

            {/* MARKET VOLUME */}

            <div
                className="
                    bg-white
                    rounded-2xl
                    border
                    border-slate-200
                    shadow-sm
                    p-4
                "
            >

                <Plot
                    data={[
                        {
                            x: ["Total Market Volume"],
                            y: [
                                data.total_market_volume,
                            ],
                            type: "bar",
                        },
                    ]}
                    layout={{
                        title:
                            "Total Market Volume",
                        autosize: true,
                        height: 400,
                    }}
                    style={{
                        width: "100%",
                    }}
                    config={{
                        responsive: true,
                    }}
                />

            </div>

        </div>

    );

};

export default MarketBreadth;